import React, { useState } from "react";
import { 
  Eye, EyeOff, Key, Check, AlertCircle, X, Lock, Shield
} from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/card";
import SharedHeader4 from "../Headers/SharedHeader4";
import SharedFooter3 from "../Footer/SharedFooter3";
import { changeUserPassword, useAppDispatch } from "../redux";
import { useSelector } from "react-redux";

const ChangePassword = () => {
  // Form state
  const { t } = useTranslation();
  const dispatch=useAppDispatch();
      const { user } = useSelector((state: any) => state.auth);

  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });

  // UI state
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
const [formErrors, setFormErrors] = useState<Record<string, any>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [changeSuccess, setChangeSuccess] = useState(false);

  // Password requirements
  const requirements = [
    { id: "length", label: "At least 8 characters", test: (pass) => pass.length >= 8 },
    // { id: "uppercase", label: "Contains uppercase letter", test: (pass) => /[A-Z]/.test(pass) },
    // { id: "lowercase", label: "Contains lowercase letter", test: (pass) => /[a-z]/.test(pass) },
    // { id: "number", label: "Contains number", test: (pass) => /\d/.test(pass) },
    // { id: "special", label: "Contains special character", test: (pass) => /[!@#$%^&*(),.?":{}|<>]/.test(pass) }
  ];


  // Validate password against requirements
  const validatePassword = (password) => {
    return requirements.map(req => ({
      ...req,
      met: req.test(password)
    }));
  };

  // Handle form submission
const handleSubmit = async (e) => {
  e.preventDefault();
  setFormErrors({});
  
  // Validation
  const errors: any = {};
  if (!formData.currentPassword) {
    errors.currentPassword = t("changePassword.currentPasswordRequired");
  }
  if (!formData.newPassword) {
    errors.newPassword = t("changePassword.newPasswordRequired");
  }
  if (formData.newPassword !== formData.confirmPassword) {
    errors.confirmPassword = t("changePassword.passwordsDoNotMatch");
  }

  const passwordValidation = validatePassword(formData.newPassword);
  if (!passwordValidation.every(req => req.met)) {
    errors.newPassword = t("changePassword.atLeast8Characters");
  }

  if (Object.keys(errors).length > 0) {
    setFormErrors(errors);
    return;
  }

  setIsSubmitting(true);

  try {
    // Prepare the payload according to the API requirements
    const payload = {
      currentPassword: formData.currentPassword,
      newPassword: formData.newPassword
    };

    // Dispatch the changeUserPassword thunk
    const result = await dispatch(changeUserPassword({ data: payload })).unwrap();
    
    // If we get here, the password update was successful
    setChangeSuccess(true);
    
    // Reset form after success
    setTimeout(() => {
      setFormData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      });
      setChangeSuccess(false);
    }, 1500);
  } catch (error) {
    // The error will be handled by the thunk's rejectWithValue
    // You can add additional error handling here if needed
    setFormErrors({ submit: t("changePassword.errorMessage") });
  } finally {
    setIsSubmitting(false);
  }
};

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col"  style={{ textAlign: "left" }}>
      <SharedHeader4 />
      
      <main className="max-w-2xl mx-auto px-4 py-8 flex-grow">
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-6">
            <Lock className="h-6 w-6 text-blue-600" />
            <h1 className="text-2xl font-bold">{t("changePassword.title")}</h1>
          </div>

          {/* Success Message */}
          {changeSuccess && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700"
            >
              <Check className="h-5 w-5" />
              <span>{t("changePassword.successMessage")}</span>
            </motion.div>
          )}

          {/* Error Message */}
          {formErrors.submit && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700"
            >
              <AlertCircle className="h-5 w-5" />
              <span>{formErrors.submit}</span>
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Current Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("changePassword.currentPassword")}
              </label>
              <div className="relative">
                <input
                  type={showCurrentPassword ? "text" : "password"}
                  value={formData.currentPassword}
                  onChange={(e) => setFormData({
                    ...formData,
                    currentPassword: e.target.value
                  })}
                  className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                    formErrors.currentPassword ? "border-red-300" : "border-gray-300"
                  }`}
                  placeholder={t("changePassword.currentPasswordPlaceholder")}
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                >
                  {showCurrentPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {formErrors.currentPassword && (
                <p className="mt-1 text-sm text-red-600">{formErrors.currentPassword}</p>
              )}
            </div>

            {/* New Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("changePassword.newPassword")}
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  value={formData.newPassword}
                  onChange={(e) => setFormData({
                    ...formData,
                    newPassword: e.target.value
                  })}
                  className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                    formErrors.newPassword ? "border-red-300" : "border-gray-300"
                  }`}
                  placeholder={t("changePassword.newPasswordPlaceholder")}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                >
                  {showNewPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {formErrors.newPassword && (
                <p className="mt-1 text-sm text-red-600">{formErrors.newPassword}</p>
              )}
            </div>

            {/* Password Requirements */}
            {/* <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">Password Requirements:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {validatePassword(formData.newPassword).map((req) => (
                  <div
                    key={req.id}
                    className={`flex items-center gap-2 text-sm ${
                      req.met ? "text-green-600" : "text-gray-500"
                    }`}
                  >
                    {req.met ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <X className="h-4 w-4" />
                    )}
                    {req.label}
                  </div>
                ))}
              </div>
            </div> */}

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("changePassword.confirmNewPassword")}
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({
                    ...formData,
                    confirmPassword: e.target.value
                  })}
                  className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                    formErrors.confirmPassword ? "border-red-300" : "border-gray-300"
                  }`}
                  placeholder={t("changePassword.confirmNewPasswordPlaceholder")}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {formErrors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{formErrors.confirmPassword}</p>
              )}
            </div>

            {/* Security Notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Shield className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <h3 className="font-medium text-blue-900">{t("changePassword.securityNotice")}</h3>
                  <p className="mt-1 text-sm text-blue-700">
                    {t("changePassword.securityNoticeMessage")}
                  </p>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full sm:w-auto"
              >
                {isSubmitting ? t("changePassword.changingPassword") : t("changePassword.changePasswordButton")}
              </Button>
            </div>
          </form>
        </Card>
      </main>
      
      <div className="mt-auto">
        <SharedFooter3 />
      </div>
    </div>
  );
};

export default ChangePassword;